import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { tenantQueries } from "@surgia/api/src/queryStore/shared/tenant";
import { hubMutations } from "@surgia/api/src/queryStore/hub";
import ProductDisplayCard from "@surgia/ui/components/productDisplayCard";
import Button from "@surgia/ui/components/button";
import { lookupQueries } from "@surgia/api/src/queryStore/shared/lookup";
import checkedIcon from "@surgia/ui/icons/checkbox/checked.svg";
import uncheckedIcon from "@surgia/ui/icons/checkbox/unchecked.svg";
import shoppingCartIcon from "@surgia/ui/icons/catalog/hub/shoppingCartBlack.svg";
import ProductFiltersModal from "../../../../admin-surgia/src/containers/productFilterModal";
import TableToolbar from "@surgia/ui/containers/tableToolBar";
import leftArrowIcon from "@surgia/ui/icons/arrows/left.svg";
import rightArrowIcon from "@surgia/ui/icons/arrows/right.svg";
import Badge from "@surgia/ui/components/badge";
import PurchaseRequestCart from "../../containers/PurchaseRequestCart";
import ProductVariationModal from "../../containers/ProductVariationsModal";
import { permissions } from "../../permissions";
import HeaderLayout from "@surgia/ui/layouts/Header";
import { useOutletContext } from "react-router-dom";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { convertFromSAR } from "../../utils/currencyConverter";
import config from "@surgia/config";
// Add auth state
const PAGE_SIZE = 4;
// Safe localStorage parsing function
// ⚠️ Keep this for non-auth data (UI preferences, etc.)
export const getLocalStorageItem = (key: string, defaultValue: any) => {
  try {
    const item = localStorage.getItem(key);
    if (item === null || item === undefined) {
      return defaultValue;
    }
    return JSON.parse(item);
  } catch (error) {
    console.warn(`Error parsing localStorage item "${key}":`, error);
    return defaultValue;
  }
};

// ✅ Migrated: tenant_id and userPermissions moved inside component to use Redux

function ProductDetails() {
  // const { t } = useTranslation();
  const authState = useSelector((state: RootState) => state.auth);
  
  // ✅ Migrated from module-level localStorage to Redux
  const tenant_id = authState.globalState.tenant_id || 0;
  const userPermissions = authState.globalState.user_permissions?.map(p => p.name) || [];
  const currencyCode = authState.globalState.currency_code || "SAR";
  const exchangeRate = authState.globalState.exchange_rate || 1;
  const [currentPage, setCurrentPage] = useState(0);
  const [search, setSearch] = useState("");
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState<any>(null);
  const [status, setStatus] = useState("");
  interface OutletContextType {
    branchesDropdown: any[];
    selectedBranch: string | null;
    setSelectedBranch: (branch: string | null) => void;
    isbranchesDropDownLoading: boolean;
  }
  const {
    branchesDropdown,
    selectedBranch,
    setSelectedBranch,
    isbranchesDropDownLoading,
  } = useOutletContext<OutletContextType>();
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  const [filters, setFilters] = useState({
    price_from: "",
    price_to: "",
    category: "",
    brandName: "", // Multiple brands (comma-separated)
    id: "",
  });

  const { data: tenantFilters } = useQuery(
    lookupQueries.queries.tenantFilters({
      tenant_id: tenant_id,
      storefront_id: import.meta.env.VITE_STORE_FRONT,
      component_id: config.componentId,
    })
  );
  const selectedBranchId =
    selectedBranch && selectedBranch?.includes(",")
      ? selectedBranch.split(",")[0]
      : selectedBranch;

  // Add the draft purchase request query
  const { data: draftPurchaseRequest } = useQuery({
    ...hubMutations.purchaseRequest.queries.getDraft({
      branchId: selectedBranchId || "",
      componentId: String(config.componentId),
      tenantId: tenant_id?.toString() || "",
    }),
    enabled: !!selectedBranchId && !!tenant_id,
  });

  // Currency is now fetched from global store (set after signin)

  const { data: tenantProductsResponse, isLoading } = useQuery({
    ...tenantQueries.queries.tenantProducts({
      component_id: config.componentId,
      name: search,
      price_from: filters?.price_from ? filters.price_from : "0",
      price_to: filters?.price_to ? filters.price_to : "1000000",
      categories:
        selectedCategories.length > 0
          ? selectedCategories.join(",")
          : undefined,
      // Use only brandName parameter
      brand_name: filters.brandName || undefined,
      page: currentPage,
      size: PAGE_SIZE,
      customer_view: true,
      tenant_id: tenant_id,
      tenant_product_name: search,
      branch_id: selectedBranchId!,
      currency_code: currencyCode,
    }),
    select: (data) => {
      // Capture exchangeRate and currencyCode in the closure
      const currentExchangeRate = exchangeRate;
      const currentCurrencyCode = currencyCode;
      
      return {
        total: data.total,
        data: data.data.map((tenant: any) => {
          // Base price is in SAR
          const basePriceInSAR = tenant.tenant_product_prices?.[0]?.price || 0;
          // Convert FROM SAR TO display currency
          const convertedPrice = convertFromSAR(basePriceInSAR, currentExchangeRate);
          
          return {
            tenant_product_id: tenant.id,
            catalog_product_id: tenant.product_id,
            name:
              tenant?.tenant_product_name && tenant?.tenant_product_name !== ""
                ? tenant?.tenant_product_name
                : tenant.catalog_info.name,
            category: {
              surgiaFirstCategory:
                tenant?.catalog_info?.product_categories?.[0]?.category?.name,
              surgiaSecondCategory:
                tenant?.catalog_info?.product_categories?.[0]?.category
                  ?.parent_category?.name,
              surgiaThirdCategory:
                tenant?.catalog_info?.product_categories?.[0]?.category
                  ?.parent_category?.parent_category?.name,
            },
            specifications:
              tenant.catalog_info.description || "No specifications",
            brand: {
              name:
                tenant.catalog_info.product_brands?.[0]?.brand_name || "Brand",
              logo: tenant.catalog_info.product_brands?.[0]?.brand_url,
            },
            price: {
              amount: convertedPrice,
              currency: currentCurrencyCode,
            },
            image: tenant.catalog_info?.product_images?.[0]?.image_path || "",
            hasVariants: tenant.catalog_info.variants_count > 1,
            variantsCount: tenant.catalog_info.variants_count,
            minimum_quantity: tenant.minimum_quantity,
            maximum_quantity: tenant.maximum_quantity,
            count_in_branch_pr: tenant.count_in_branch_pr,
            feature_values: tenant.catalog_info.feature_values,
          };
        }),
      };
    },
    enabled: !!selectedBranchId && !!tenant_id,
  });
  // constants
  const totalPages = Math.ceil((tenantProductsResponse?.total || 0) / 4);

  // handlers
  const handlePriceFilter = (from: string, to: string) => {
    setFilters((prev) => ({ ...prev, price_from: from, price_to: to }));
    setCurrentPage(0);
  };

  const handleShowProductVariations = (product: any) => {
    setSelectedProduct(product);
    setSelectedProductId(product.tenant_product_id);
    setShowVariationModal(true);
  };
  const handleBrandToggle = (brandName: string) => {
    setFilters((prev) => {
      const currentBrands = prev.brandName ? prev.brandName.split(",") : [];

      if (currentBrands.includes(brandName)) {
        // Remove brand
        const updatedBrands = currentBrands.filter(
          (brand) => brand !== brandName
        );
        return {
          ...prev,
          brandName: updatedBrands.join(","),
        };
      } else {
        // Add brand
        const updatedBrands = [...currentBrands, brandName];
        return {
          ...prev,
          brandName: updatedBrands.join(","),
        };
      }
    });
    setCurrentPage(0);
  };

  const clearAllFilters = () => {
    setSearch("");
    setSelectedCategories([]);
    setFilters({
      price_from: "",
      price_to: "",
      category: "",
      brandName: "",
      id: "",
    });
    setCurrentPage(0);
  };

  const [showPurchaseCart, setShowPurchaseCart] = useState(false);
  const [showVariationModal, setShowVariationModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);

  const handleOpenPurchaseCart = () => {
    setShowPurchaseCart(true);
  };

  return (
    <div className="min-h-screen">
      {/* Header */}
      <HeaderLayout
        subDivAdditionalStyles="!flex-row"
        title="Catalog"
        subtitle="Browse and place purchase request."
        additionalStyles="justify-between"
        showBranchesDropDown={authState?.globalState?.user_branches?.includes(
          ","
        )}
        branches={branchesDropdown}
        selectedBranch={selectedBranch}
        setSelectedBranch={setSelectedBranch}
        showAllBranches={false}
        isBranchesLoading={isbranchesDropDownLoading}
      >
        {userPermissions.includes(permissions.VIEW_DRAFT_PURCHASE_REQUEST) && (
          <button
            onClick={handleOpenPurchaseCart}
            className="flex items-center gap-2 p-2 hover:bg-gray-100 rounded-lg transition-colors relative"
          >
            <img src={shoppingCartIcon} alt="Cart" className="w-6 h-6" />
            {/* Show draft count if there are items */}
            {draftPurchaseRequest?.pr_items &&
              draftPurchaseRequest.pr_items.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                  {draftPurchaseRequest.pr_items.length}
                </span>
              )}
          </button>
        )}
      </HeaderLayout>

      <div className="mx-auto pt-6">
        <div className="flex gap-1 sm:gap-2 md:gap-3  xl:gap-[1.5rem]">
          {/* Left Sidebar - Filters */}
          {tenantFilters?.data?.["brands"] &&
            tenantFilters?.data?.["brands"]?.length > 0 && (
              <div className="w-fit flex-shrink-0 hidden min-[1200px]:block   ">
                <div className=" py-4 pl-1 space-y-6 ">
                  {/* Brand Filter */}
                  <div>
                    <h3 className="text-sm font-medium text-gray-900 mb-3">
                      Brand
                    </h3>
                    <div className="space-y-2 max-h-48 overflow-y-auto hide-scrollbar">
                      {tenantFilters?.data?.["brands"]?.map(
                        (brandName: string, index: number) => {
                          // Check if this brand is selected
                          const currentBrands = filters.brandName
                            ? filters.brandName.split(",")
                            : [];
                          const isSelected = currentBrands.includes(brandName);

                          return (
                            <label
                              key={index}
                              className="flex items-center cursor-pointer"
                            >
                              <img
                                src={isSelected ? checkedIcon : uncheckedIcon}
                                alt="checkbox"
                                onClick={() => handleBrandToggle(brandName)}
                                className="w-5 h-5 cursor-pointer"
                              />
                              <span className="ml-2 text-sm text-gray-700">
                                {brandName}
                              </span>
                            </label>
                          );
                        }
                      )}
                    </div>
                  </div>

                  {/* Price Range Filter */}
                  {userPermissions.includes(permissions.VIEW_PRODUCT_PRICE) && (
                    <div>
                      <h3 className="text-sm font-medium text-gray-900 mb-3">
                        Price range
                      </h3>
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex flex-col gap-1.5">
                          <input
                            type="number"
                            placeholder="0"
                            className="w-[84.5px] h-10 px-3 py-2 text-sm border border-[#D5D7DA] rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            onChange={(e) =>
                              handlePriceFilter(
                                e.target.value,
                                filters.price_to
                              )
                            }
                          />
                        </div>
                        <span className="text-[#A4A7AE] text-sm font-medium">
                          -
                        </span>
                        <div className="flex flex-col gap-1.5">
                          <input
                            type="number"
                            placeholder="12"
                            className="w-[84.5px] h-10 px-3 py-2 text-sm border border-[#D5D7DA] rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            onChange={(e) =>
                              handlePriceFilter(
                                filters.price_from,
                                e.target.value
                              )
                            }
                          />
                        </div>
                      </div>
                    </div>
                  )}
                  <Button
                    variant="outline"
                    onClick={clearAllFilters}
                    className="px-4 py-2 !mx-auto"
                  >
                    Reset
                  </Button>
                </div>
              </div>
            )}

          {/* Right Main Area */}
          <div className="flex-1 border border-gray-200 border-t-0 rounded-lg flex flex-col">
            {/* Header with search and filters */}
            <div className="bg-white border-b border-t rounded-t-lg border-gray-200 p-4">
              <div className="flex items-center justify-between ">
                <div className="flex items-center gap-4">
                  <h2 className="text-lg font-semibold text-gray-900">
                    Products
                  </h2>
                  <Badge variant="default" className="bg-white !rounded-md">
                    {tenantProductsResponse?.total || 0}
                  </Badge>
                </div>
                <div className="">
                  <TableToolbar
                    additionalFilterStyle="min-[1200px]:hidden"
                    mainContainerStyle="!mb-0"
                    statusTabs={[]}
                    currentStatus={status}
                    onStatusChange={(value) => setStatus(value || "")}
                    searchValue={search}
                    onSearchChange={setSearch}
                    onFilterClick={() => setFiltersModalOpen(!filtersModalOpen)}
                    showSearch={true}
                  />
                </div>
              </div>
            </div>

            {/* Product Grid */}
            <div className="flex-1 p-6 min-h-0 flex flex-col">
              {isLoading ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-500">Loading products...</p>
                  </div>
                </div>
              ) : (
                <>
                  {tenantProductsResponse?.data &&
                  tenantProductsResponse.data?.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6 min-h-full">
                      {tenantProductsResponse.data.map((product) => (
                        <ProductDisplayCard
                          permissions={userPermissions}
                          key={product.tenant_product_id}
                          product={product}
                          className="w-full"
                          showProductVariations={handleShowProductVariations}
                          selectedBranchId={selectedBranchId!}
                          tenant_id={tenant_id?.toString() || ""}
                          draftPurchaseRequestID={
                            draftPurchaseRequest?.id?.toString()!
                          }
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="flex-1 flex items-center justify-center">
                      <div className="text-center">
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                          No products found
                        </h3>
                        <p className="text-gray-500 mb-4">
                          Try adjusting your search or filter criteria
                        </p>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Pagination */}
            {totalPages >= 1 && (
              <div className="flex items-center justify-center py-3 px-6 gap-3 border-t border-[#E9EAEB]">
                <Button
                  variant="outline"
                  onClick={() =>
                    setCurrentPage((prev) => Math.max(0, prev - 1))
                  }
                  disabled={currentPage === 0}
                  className="flex items-center gap-2 px-4 py-2 text-sm border border-[#D5D7DA] rounded-lg bg-white text-[#535862] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <img
                    src={leftArrowIcon}
                    alt="Previous"
                    className="w-3.5 h-3.5"
                  />
                  Previous
                </Button>

                <div className="flex gap-3">
                  {Array.from({ length: Math.min(6, totalPages) }, (_, i) => (
                    <button
                      key={i}
                      onClick={() => setCurrentPage(i)}
                      className={`w-10 h-10 rounded-lg text-sm font-medium transition-colors flex items-center justify-center ${
                        currentPage === i
                          ? "bg-[#FAFAFA] text-[#535862] border border-[#D5D7DA]"
                          : "text-[#535862] hover:bg-gray-50"
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                </div>

                <Button
                  variant="outline"
                  onClick={() =>
                    setCurrentPage((prev) => Math.min(totalPages - 1, prev + 1))
                  }
                  disabled={currentPage === totalPages - 1}
                  className="flex items-center gap-2 px-4 py-2 text-sm border border-[#D5D7DA] rounded-lg bg-white text-[#535862] hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                  <img
                    src={rightArrowIcon}
                    alt="Next"
                    className="w-3.5 h-3.5"
                  />
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      {}
      <ProductFiltersModal
        open={filtersModalOpen}
        onClose={() => setFiltersModalOpen(false)}
        onApply={() => {
          setFiltersModalOpen(false);
          // Any additional logic you need
        }}
        showPriceRange={userPermissions.includes(
          permissions.VIEW_PRODUCT_PRICE
        )}
        filters={filters}
        setFilters={(newFilters) =>
          setFilters({
            price_from: newFilters.price_from || "",
            price_to: newFilters.price_to || "",
            category: newFilters.category || "",
            brandName: newFilters.brandName || "",
            id: newFilters.id || "",
          })
        }
        tenantId={tenant_id?.toString() || ""}
        showCategories={false}
      />

      {/* Product Variation Modal */}
      {selectedProduct && (
        <ProductVariationModal
          key={selectedProductId}
          tenant_id={tenant_id?.toString() || ""}
          open={showVariationModal}
          onClose={() => setShowVariationModal(false)}
          product={{
            id: selectedProduct.tenant_product_id,
            name: selectedProduct.name,
            category: [
              selectedProduct.category.surgiaFirstCategory,
              selectedProduct.category.surgiaSecondCategory,
              selectedProduct.category.surgiaThirdCategory,
            ],
            brand: selectedProduct.brand,
            image: selectedProduct.image,
            price: selectedProduct.price,
            minimum_quantity: selectedProduct.minimum_quantity,
            maximum_quantity: selectedProduct.maximum_quantity,
          }}
          // onAddToPR={handleAddToPRFromVariation}
          setSelectedProductId={setSelectedProductId}
          selectedProductId={selectedProductId}
          draftPurchaseRequestID={draftPurchaseRequest?.id!}
        />
      )}

      {/* Purchase Cart Modal */}
      <PurchaseRequestCart
        open={showPurchaseCart}
        onClose={() => setShowPurchaseCart(false)}
        cartItems={draftPurchaseRequest?.pr_items || []}
        branchId={Number(selectedBranchId)}
        draftPurchaseRequestID={draftPurchaseRequest?.id!}
      />
    </div>
  );
}

export default ProductDetails;
